import { useQuery } from '@tanstack/react-query';
import { licenseApi } from '../api/endpoints/licenseApi';
import { useAuth } from '../contexts/AuthContext';

/**
 * ON-13 — License status hook.
 * Polls the /api/license/status endpoint every 5 min when the user is
 * authenticated. staleTime 60s prevents redundant calls when multiple
 * components subscribe simultaneously.
 *
 * Returns: { data, isLoading, isError, refetch }
 * data shape: { rung, expiry_date, days_left, entitled_modules,
 *               instance_fingerprint, heartbeat_result }
 */
export default function useLicenseStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['license', 'status'],
    queryFn: async () => {
      const result = await licenseApi.getStatus();
      return result.data?.data || result.data || {};
    },
    enabled: Boolean(user),
    refetchInterval: 5 * 60 * 1000,   // 5 minutes
    staleTime: 60 * 1000,              // 60 seconds
    // Soft-fail: never throw on network errors — the license state should
    // degrade gracefully rather than crash the entire app shell.
    retry: 1,
  });
}

/**
 * Lightweight read-only variant — returns only { rung, days_left }
 * without triggering a re-render on unrelated fields.
 */
export function useLicenseStatusReadOnly() {
  const query = useLicenseStatus();
  return {
    rung: query.data?.rung ?? null,
    daysLeft: query.data?.days_left ?? null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
